// Omnichannel — Kanal bağlantıları (personel): "Facebook sayfası bağla" akışının panel denetleyicisi.
//
//   GET  /api/v2/omni/connections · /api/v2/omni/meta-connect/tenants
//   POST /api/v2/omni/meta-connect/start → Facebook izin ekranı → dönüş /dashboard?kanal_baglanti=<sid>&sonuc=<kod>
//   GET  /api/v2/omni/meta-connect/sessions/:sid → sayfa seçimi → POST …/finalize
//   POST /api/v2/omni/connections/:id/verify · /disconnect
//
// Arayüz yalnız kolaylık sağlar; bütün kapılar (operatör, CSRF, izinler, çakışmalar, şifreleme) sunucuda.
// Görünüm (saf): ./kanal-baglantilari-gorunum.ts

import { apiFetch, apiGetJson } from '../api.js';
import {
  DONUS_MESAJLARI, dogrulamaOzeti, hataMetni, kanalBaglantilariHtml,
  type BaglamaOturumu, type DogrulamaSonucu, type IstatistikSonucu, type KanalBaglantilariDurumu, type KanalBaglantisi,
  type KanalSecimi, type Tenant,
} from './kanal-baglantilari-gorunum.js';

const SID_RE = /^cred_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const durum: KanalBaglantilariDurumu = {
  yukleniyor: true,
  hata: null,
  baglantilar: [],
  tenantlar: [],
  oturum: null,
  bilgi: null,
  dogrulama: {},
  mesgul: false,
};
let bekleyenSid: string | null = null;

/** Formun kalıcı seçimi; yoksa boş başlatılır. */
function secim(): KanalSecimi {
  if (!durum.secim) durum.secim = { sayfa: '', tenant: '', instagram: null, onay: false };
  return durum.secim;
}

/** Facebook dönüşü panele ?kanal_baglanti=&sonuc= ile gelir; okunur ve adres çubuğundan silinir. */
export function kanalBaglantiDonusuVarMi(): boolean {
  try { return new URLSearchParams(location.search).has('kanal_baglanti'); } catch { return false; }
}

function donusParametreleriniAl(): void {
  try {
    const p = new URLSearchParams(location.search);
    if (!p.has('kanal_baglanti') && !p.has('sonuc')) return;
    const sonuc = p.get('sonuc') ?? '';
    const sid = p.get('kanal_baglanti') ?? '';
    durum.bilgi = DONUS_MESAJLARI[sonuc] ?? { tur: 'hata', metin: 'Bilinmeyen bağlama sonucu.' };
    bekleyenSid = sonuc === 'secim_bekliyor' && SID_RE.test(sid) ? sid : null;
    p.delete('kanal_baglanti');
    p.delete('sonuc');
    const q = p.toString();
    history.replaceState(null, '', `${location.pathname}${q ? `?${q}` : ''}${location.hash}`);
  } catch { /* adres okunamazsa yok say */ }
}

type PostSonucu<T> = { ok: true; data: T } | { ok: false; hata: string; ayrinti?: { eksik?: unknown } };

async function postJson<T>(url: string, govde: unknown): Promise<PostSonucu<T>> {
  try {
    const res = await apiFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(govde ?? {}) });
    // Sunucu alanları en üst düzeyde döner ({ ok: true, ...alanlar }) — apiGetJson ile aynı sözleşme.
    const j = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; ayrinti?: { eksik?: unknown } };
    if (res.ok && j.ok === true) return { ok: true, data: j as unknown as T };
    return { ok: false, hata: String(j.error || `HTTP ${res.status}`), ayrinti: j.ayrinti };
  } catch (e) {
    return { ok: false, hata: (e as Error).message || 'baglanti_hatasi' };
  }
}

export async function loadKanalBaglantilari(): Promise<void> {
  donusParametreleriniAl();
  durum.yukleniyor = true;
  durum.hata = null;
  const [b, t] = await Promise.all([
    apiGetJson<{ baglantilar: KanalBaglantisi[] }>('/api/v2/omni/connections').catch(() => null),
    apiGetJson<{ tenantlar: Tenant[] }>('/api/v2/omni/meta-connect/tenants').catch(() => null),
  ]);
  durum.yukleniyor = false;
  if (b && b.ok) durum.baglantilar = b.data.baglantilar;
  else durum.hata = 'Bağlantılar okunamadı.';
  if (t && t.ok) durum.tenantlar = t.data.tenantlar;

  if (bekleyenSid) {
    const sid = bekleyenSid;
    bekleyenSid = null;
    const o = await apiGetJson<Omit<BaglamaOturumu, 'sid'>>(`/api/v2/omni/meta-connect/sessions/${encodeURIComponent(sid)}`).catch(() => null);
    if (o && o.ok) durum.oturum = { sid, ...o.data };
    else durum.bilgi = { tur: 'hata', metin: hataMetni(o && !o.ok ? o.error : 'oturum_yok') };
  }
}

export function renderKanalBaglantilari(): void {
  const el = document.getElementById('view-kanal-baglantilari');
  if (!el) return;
  el.innerHTML = kanalBaglantilariHtml(durum);
  if (el.dataset.kbBagli !== '1') {
    el.dataset.kbBagli = '1';
    el.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-eylem]');
      if (btn) void eylem(btn, el);
    });
    // Seçim duruma yazılır: panel yenilemesi görünümü baştan çizse de seçim kaybolmaz.
    el.addEventListener('change', (ev) => {
      const t = ev.target as HTMLInputElement | HTMLSelectElement | null;
      if (!t || !durum.oturum) return;
      const s = secim();
      if (t instanceof HTMLInputElement && t.name === 'kb-sayfa') s.sayfa = t.value;
      else if (t.id === 'kb-tenant') s.tenant = t.value;
      else if (t instanceof HTMLInputElement && t.id === 'kb-instagram') s.instagram = t.checked;
      else return;
      // Onay adımı açıkken seçim değişirse onay geri alınır; kullanıcı yeni özeti görmeden bağlanmaz.
      if (s.onay) { s.onay = false; renderKanalBaglantilari(); }
    });
  }
}

async function eylem(btn: HTMLElement, el: HTMLElement): Promise<void> {
  if (durum.mesgul) return;
  const e = btn.dataset.eylem;
  const id = btn.dataset.id ?? '';

  if (e === 'baslat') {
    durum.mesgul = true;
    renderKanalBaglantilari();
    const r = await postJson<{ authorizeUrl: string }>('/api/v2/omni/meta-connect/start', {});
    durum.mesgul = false;
    if (r.ok && /^https:\/\/www\.facebook\.com\//.test(r.data.authorizeUrl)) {
      location.assign(r.data.authorizeUrl);
      return;
    }
    durum.bilgi = { tur: 'hata', metin: hataMetni(r.ok ? 'meta_hatasi' : r.hata) };
    renderKanalBaglantilari();
    return;
  }

  if (e === 'vazgec') {
    durum.oturum = null;
    durum.bilgi = null;
    durum.secim = undefined;
    renderKanalBaglantilari();
    return;
  }

  if (e === 'onay-geri') {
    secim().onay = false;
    renderKanalBaglantilari();
    return;
  }

  if (e === 'tamamla' && durum.oturum) {
    // Değerler önce durumdan, yoksa formdan (change olayı kaçtıysa) okunur.
    const s = secim();
    s.sayfa = s.sayfa || (el.querySelector<HTMLInputElement>('input[name="kb-sayfa"]:checked')?.value ?? '');
    s.tenant = s.tenant || (el.querySelector<HTMLSelectElement>('#kb-tenant')?.value ?? '');
    if (s.instagram === null) s.instagram = el.querySelector<HTMLInputElement>('#kb-instagram')?.checked === true;
    if (!s.sayfa || !s.tenant) {
      durum.bilgi = { tur: 'uyari', metin: 'Bir sayfa ve bir çalışma alanı (marka) seçin.' };
      renderKanalBaglantilari();
      return;
    }
    // Tarayıcı confirm() yerine sayfa içi onay adımı (uygulama içi tarayıcı confirm'i göstermeden reddediyordu).
    s.onay = true;
    renderKanalBaglantilari();
    return;
  }

  if (e === 'onayla' && durum.oturum && durum.secim?.onay) {
    const { sayfa, tenant } = durum.secim;
    const instagram = durum.secim.instagram === true;
    if (!sayfa || !tenant) { durum.secim.onay = false; renderKanalBaglantilari(); return; }
    durum.mesgul = true;
    renderKanalBaglantilari();
    const r = await postJson<{ abonelik: string }>(
      `/api/v2/omni/meta-connect/sessions/${encodeURIComponent(durum.oturum.sid)}/finalize`,
      { pageId: sayfa, tenantId: tenant, instagram },
    );
    durum.mesgul = false;
    if (r.ok) {
      durum.oturum = null;
      durum.secim = undefined;
      durum.bilgi = r.data.abonelik === 'abonelik_basarisiz'
        ? { tur: 'uyari', metin: 'Sayfa bağlandı ama webhook aboneliği kurulamadı; "Doğrula" ile kontrol edin.' }
        : { tur: 'basari', metin: 'Sayfa bağlandı. Anahtar şifreli kasada.' };
      await loadKanalBaglantilari();
    } else {
      const eksik = Array.isArray(r.ayrinti?.eksik) ? ` Eksik: ${(r.ayrinti?.eksik as unknown[]).map(String).join(', ')}` : '';
      durum.bilgi = { tur: 'hata', metin: `${hataMetni(r.hata)}${eksik}` };
    }
    renderKanalBaglantilari();
    return;
  }

  if (e === 'dogrula' && id) {
    durum.mesgul = true;
    durum.dogrulama[id] = 'Doğrulanıyor…';
    renderKanalBaglantilari();
    const r = await postJson<DogrulamaSonucu>(`/api/v2/omni/connections/${encodeURIComponent(id)}/verify`, {});
    durum.mesgul = false;
    durum.dogrulama[id] = r.ok ? dogrulamaOzeti(r.data) : hataMetni(r.hata);
    renderKanalBaglantilari();
    return;
  }

  if (e === 'istatistik' && id) {
    if (!durum.istatistik) durum.istatistik = {};
    durum.istatistik[id] = 'İstatistikler okunuyor…';
    renderKanalBaglantilari();
    try {
      const r = await apiGetJson<IstatistikSonucu>(`/api/v2/omni/connections/${encodeURIComponent(id)}/istatistik`);
      durum.istatistik[id] = r.ok ? r.data : hataMetni(r.error);
    } catch {
      durum.istatistik[id] = 'İstatistik okunamadı.';
    }
    renderKanalBaglantilari();
    return;
  }

  if (e === 'kes' && id) {
    const soru = 'Bağlantı kesilecek: bu sayfanın (ve aynı anahtarı kullanan Instagram hesabının) mesajları artık gelen '
      + 'kutusuna düşmez ve yanıt gönderilemez. Anahtar kalıcı olarak silinir. Devam edilsin mi?';
    if (!window.confirm(soru)) return;
    durum.mesgul = true;
    renderKanalBaglantilari();
    const r = await postJson<{ kesilen: unknown[] }>(`/api/v2/omni/connections/${encodeURIComponent(id)}/disconnect`, { onay: true, aboneligiKaldir: false });
    durum.mesgul = false;
    durum.bilgi = r.ok ? { tur: 'basari', metin: 'Bağlantı kesildi, anahtar imha edildi.' } : { tur: 'hata', metin: hataMetni(r.hata) };
    if (r.ok) await loadKanalBaglantilari();
    renderKanalBaglantilari();
  }
}
